import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { CustomerDetailsSection } from '../../../../resources/base/models/enums';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import {
  MaritalStatus,
  MonthlyPropertyCharge,
  NetIncomeAmount,
  NumberOfDependentChildren,
  OtherIncome,
  OtherMonthlyCosts,
  ProfessionEntrepreneur,
  ResidenceStatus,
} from '../../utils/fieldsBusinessCustomer';
import { CustomerDetailsFormGroupType, patchCreditApplicationAndUpdateState, useFormSectionHelpers } from '../utils';
import { AdditionalInfoDataFormType, getAdditionalInfoDataFormInitialValues } from './initialValues';
import { additionalInfoDataFormRequestMapper } from './requestMapper';
import { getAdditionalInfoDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsSection;
  formGroup: CustomerDetailsFormGroupType;
}

export const AdditionalInfoDataForm: FunctionComponent<Props> = ({ formName, formGroup }) => {
  const [initialValues, setInitialValues] = useState({} as AdditionalInfoDataFormType);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<AdditionalInfoDataFormType>>();

  const creditApplicantCustomerData = useSelector(
    creditApplicationSelectors.selectCreditApplicantCustomerData
  ) as CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const navigate = useNavigate();

  useEffect(() => {
    setInitialValues(getAdditionalInfoDataFormInitialValues(creditApplicantCustomerData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData]);

  useEffect(() => {
    setValidationSchema(getAdditionalInfoDataFormValidationSchema(t));
  }, [t]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    formGroup,
    formName
  );

  const onSubmit = (values: AdditionalInfoDataFormType) => {
    dispatch(loadingActions.setLoading(true));
    patchCreditApplicationAndUpdateState(additionalInfoDataFormRequestMapper(values, creditApplicantCustomerData))
      .then(onFormSubmitSuccess)
      .catch(navigate.error)
      .finally(() => dispatch(loadingActions.setLoading(false)));
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:additionalInformation')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent onFormInitialValuesChange={onFormInitialValuesChange} />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
}

const FormContent: FunctionComponent<FormContentProps> = ({ onFormInitialValuesChange }) => {
  const { t } = useTranslation();
  const { submitForm, initialValues, validateForm } = useFormikContext<AdditionalInfoDataFormType>();

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/3" m="1/2" s="1/1">
            <ProfessionEntrepreneur t={t} isSoleTrader />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/2" s="1/1">
            <ResidenceStatus t={t} />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/2" s="1/1">
            <MaritalStatus t={t} />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/2" s="1/1">
            <NumberOfDependentChildren t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" s="1/1">
            <NetIncomeAmount t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" s="1/1">
            <OtherIncome t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" s="1/1">
            <MonthlyPropertyCharge t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" s="1/1">
            <OtherMonthlyCosts t={t} />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>
      <ButtonContainer right>
        <Layout right>
          <Layout.Item default="1/3" m="2/5" s="2/3">
            <Button full type="submit" onClick={submitForm}>
              {t('step3:navigation:continue')}
            </Button>
          </Layout.Item>
        </Layout>
      </ButtonContainer>
    </Fieldset>
  );
};
